import type { CodeMetadataItem, CodeMetadataRequest, CodeMetadataResult, CodeSystem } from '../types';
import { extractionApi } from './extractionApi';

const metadataCache = new Map<string, CodeMetadataResult>();
const inflightRequests = new Map<string, Promise<CodeMetadataResult[]>>();

const normalizeCode = (code: string) => (code || '').trim().toUpperCase().replace(/\s+/g, '');
const normalizeLocale = (locale?: string) => (locale || '').trim().toLowerCase();

export const makeCodeMetadataKey = (system: CodeSystem, code: string, locale?: string) => {
  return `${system}|${normalizeCode(code)}|${normalizeLocale(locale)}`;
};

const normalizeItem = (item: CodeMetadataItem, defaultLocale?: string) => {
  const system = item.system;
  const code = normalizeCode(item.code);
  if (!system || !code) return null;
  return {
    code,
    system,
    locale: item.locale || defaultLocale || undefined,
  } as Required<CodeMetadataItem>;
};

export const getCachedCodeMetadata = (items: CodeMetadataItem[], defaultLocale?: string) => {
  const out: Record<string, CodeMetadataResult> = {};
  items.forEach((item) => {
    const normalized = normalizeItem(item, defaultLocale);
    if (!normalized) return;
    const key = makeCodeMetadataKey(normalized.system, normalized.code, normalized.locale);
    const cached = metadataCache.get(key);
    if (cached) out[key] = cached;
  });
  return out;
};

export const getMissingCodeMetadataItems = (items: CodeMetadataItem[], defaultLocale?: string) => {
  const seen = new Set<string>();
  const missing: Required<CodeMetadataItem>[] = [];
  items.forEach((item) => {
    const normalized = normalizeItem(item, defaultLocale);
    if (!normalized) return;
    const key = makeCodeMetadataKey(normalized.system, normalized.code, normalized.locale);
    if (seen.has(key)) return;
    seen.add(key);
    if (!metadataCache.has(key)) {
      missing.push(normalized);
    }
  });
  return missing;
};

const chunkItems = <T,>(items: T[], size: number) => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

export const fetchAndCacheCodeMetadata = async (
  items: CodeMetadataItem[],
  defaultLocale?: string
) => {
  const normalizedItems = getMissingCodeMetadataItems(items, defaultLocale);
  if (normalizedItems.length === 0) return [];

  const chunks = chunkItems(normalizedItems, 100);
  const results: CodeMetadataResult[] = [];

  for (const chunk of chunks) {
    const signature = chunk
      .map((item) => `${item.system}:${item.code}:${normalizeLocale(item.locale)}`)
      .sort()
      .join('|');
    const batchKey = `${normalizeLocale(defaultLocale)}|${signature}`;

    let inflight = inflightRequests.get(batchKey);
    if (!inflight) {
      const payload: CodeMetadataRequest = {
        locale: defaultLocale,
        codes: chunk.map((item) => ({
          code: item.code,
          system: item.system,
          locale: item.locale,
        })),
      };
      inflight = extractionApi.fetchCodesMetadata(payload).then((response) => {
        const responseItems = response?.results || [];
        responseItems.forEach((entry) => {
          const key = makeCodeMetadataKey(
            entry.system,
            entry.code,
            entry.locale_used || defaultLocale
          );
          metadataCache.set(key, entry);
        });
        return responseItems;
      }).finally(() => {
        inflightRequests.delete(batchKey);
      });
      inflightRequests.set(batchKey, inflight);
    }

    const resolved = await inflight;
    results.push(...resolved);
  }

  return results;
};
